import React, { useEffect, createElement } from 'react';
import { useLanguage } from '../context/LanguageContext';
export function SEOHead() {
  const {
    language
  } = useLanguage();
  useEffect(() => {
    // --- Page Title ---
    document.title = language === 'es' ? 'Lorena Beauty House | Tratamientos Faciales y Corporales en Queens, NY' : 'Lorena Beauty House | Facial & Body Treatments in Queens, NY';
    // --- Meta Tags ---
    const metaTags: Record<string, string> = {
      description: language === 'es' ? 'Lorena Beauty House — Centro de estética profesional en East Elmhurst, Queens NY. Tratamientos faciales personalizados, limpieza profunda, tratamiento de acné, manchas, celulitis y más. Productos propios Lorena Beauty Skin Care. Agenda tu cita hoy.' : 'Lorena Beauty House — Professional skincare center in East Elmhurst, Queens NY. Personalized facial treatments, deep cleansing, acne treatment, dark spots, cellulite and more. Proprietary Lorena Beauty Skin Care products. Book your appointment today.',
      keywords: 'Lorena Beauty House, facial treatment Queens NY, tratamiento facial Queens, esteticista East Elmhurst, skin care Queens New York, acne treatment Queens, limpieza facial Queens, Lorena Hernandez, beauty salon East Elmhurst, spa Queens NY, tratamiento de manchas, celulitis, Lorena Beauty Skin Care',
      author: 'Lorena Beauty House',
      robots: 'index, follow',
      'geo.region': 'US-NY',
      'geo.placename': 'East Elmhurst, Queens, New York',
      'geo.position': '40.7631;-73.8654',
      ICBM: '40.7631, -73.8654',
      // Open Graph
      'og:title': 'Lorena Beauty House | Queens, NY',
      'og:description': language === 'es' ? 'Centro de estética profesional en Queens, NY. Tratamientos faciales personalizados con productos propios. Agenda tu cita.' : 'Professional skincare center in Queens, NY. Personalized facial treatments with proprietary products. Book your appointment.',
      'og:type': 'website',
      'og:locale': language === 'es' ? 'es_US' : 'en_US',
      'og:site_name': 'Lorena Beauty House',
      'og:image': 'https://cdn.magicpatterns.com/uploads/jYmD6asbvXQTBHDRJHXdMo/LOGO_DE_LORENA_BEAUTY.png',
      // Twitter Card
      'twitter:card': 'summary_large_image',
      'twitter:title': 'Lorena Beauty House | Queens, NY',
      'twitter:description': language === 'es' ? 'Tratamientos faciales y corporales personalizados en Queens, NY.' : 'Personalized facial and body treatments in Queens, NY.',
      'twitter:image': 'https://cdn.magicpatterns.com/uploads/jYmD6asbvXQTBHDRJHXdMo/LOGO_DE_LORENA_BEAUTY.png'
    };
    // Set or update meta tags
    Object.entries(metaTags).forEach(([name, content]) => {
      const isOg = name.startsWith('og:');
      const isTwitter = name.startsWith('twitter:');
      const attr = isOg || isTwitter ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    });
    // --- HTML lang attribute ---
    document.documentElement.lang = language === 'es' ? 'es' : 'en';
    // --- Canonical URL ---
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = window.location.origin + window.location.pathname;
    // --- JSON-LD: LocalBusiness Schema ---
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'BeautySalon',
      name: 'Lorena Beauty House',
      alternateName: 'Lorena Beauty Skin Care',
      description: 'Professional skincare center specializing in personalized facial and body treatments. Proprietary Lorena Beauty Skin Care product line.',
      url: window.location.origin,
      telephone: ['+1-347-264-8498', '+1-917-396-4125'],
      address: {
        '@type': 'PostalAddress',
        streetAddress: '32-60 84th St',
        addressLocality: 'East Elmhurst',
        addressRegion: 'NY',
        postalCode: '11370',
        addressCountry: 'US'
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 40.7631,
        longitude: -73.8654
      },
      image: 'https://cdn.magicpatterns.com/uploads/jYmD6asbvXQTBHDRJHXdMo/LOGO_DE_LORENA_BEAUTY.png',
      priceRange: '$$',
      openingHoursSpecification: {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '19:00'
      },
      founder: {
        '@type': 'Person',
        name: 'Lorena Hernández',
        jobTitle: 'Licensed Esthetician & Founder'
      },
      areaServed: {
        '@type': 'City',
        name: 'Queens, New York'
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Beauty Services',
        itemListElement: [{
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Facial Treatment'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Acne Treatment'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Deep Cleansing Facial'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Body Treatments'
          }
        }, {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Dark Spot Treatment'
          }
        }]
      },
      sameAs: []
    };
    // --- JSON-LD: FAQPage Schema ---
    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [{
        '@type': 'Question',
        name: '¿Necesito una consulta antes de mi primer tratamiento?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, cada clienta nueva recibe una evaluación personalizada para determinar el mejor plan de tratamiento para su tipo de piel.'
        }
      }, {
        '@type': 'Question',
        name: '¿Cuánto tiempo toma ver resultados?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Los resultados varían según el tratamiento y la condición de la piel. Muchas clientas notan mejoras desde la primera sesión.'
        }
      }, {
        '@type': 'Question',
        name: 'Where is Lorena Beauty House located?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Lorena Beauty House is located at 32-60 84th St, East Elmhurst, NY 11370, in Queens, New York.'
        }
      }, {
        '@type': 'Question',
        name: 'How do I book an appointment?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You can book an appointment by calling (347) 264-8498 or (917) 396-4125, or by sending a WhatsApp message.'
        }
      }]
    };
    // Inject or update JSON-LD scripts
    const injectJsonLd = (id: string, data: object) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    injectJsonLd('schema-local-business', localBusinessSchema);
    injectJsonLd('schema-faq', faqSchema);
  }, [language]);
  // This component renders nothing — it only manages the <head>
  return null;
}